import type { PcV2Channel, PcV2Entry } from './typesV2';

/** Channel label + direction, in the words the front desk would use. */
export function pcV2Label(entry: PcV2Entry): string {
  if (entry.channel === 'call') {
    return entry.direction === 'in' ? 'Call · Inbound' : 'Call · Outbound';
  }
  const channel = entry.channel === 'sms' ? 'Text' : 'Email';
  return `${channel} · ${entry.direction === 'in' ? 'Received' : 'Sent'}`;
}

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
});

/** "May 21, 2026 · 9:04 AM" — absolute, because the list is read by date. */
export function pcV2FormatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DATE_FMT.format(d)} · ${TIME_FMT.format(d)}`;
}

/** Newest first. The only ordering this tab has. */
export function pcV2SortEntries(entries: PcV2Entry[]): PcV2Entry[] {
  return [...entries].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

export const PC_V2_CHANNELS: Record<PcV2Channel, { label: string; icon: 'mail' | 'chat' | 'phone'; color: string }> = {
  email: { label: 'Email', icon: 'mail', color: 'var(--info)' },
  sms: { label: 'Text', icon: 'chat', color: 'var(--success)' },
  call: { label: 'Call', icon: 'phone', color: 'var(--gold)' },
};

/** Tile order — texts first, because that is where most of the history is. */
export const PC_V2_CHANNEL_ORDER: PcV2Channel[] = ['sms', 'call', 'email'];

/** Plural tile labels. "Texts · 12" reads better than "Text · 12". */
export const PC_V2_CHANNEL_PLURAL: Record<PcV2Channel, string> = {
  sms: 'Texts',
  call: 'Calls',
  email: 'Emails',
};

/**
 * All-time count per channel. Always returns all three keys, including zeros —
 * a channel that disappears when it has no rows makes the tiles move, and the
 * absence of a channel is itself worth seeing.
 */
export function pcV2CountsByChannel(entries: PcV2Entry[]): Record<PcV2Channel, number> {
  const counts: Record<PcV2Channel, number> = { sms: 0, call: 0, email: 0 };
  for (const entry of entries) counts[entry.channel] += 1;
  return counts;
}
